import * as PIXI from 'pixi.js';
import { GRID_WIDTH, GRID_HEIGHT, CELL_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT } from '../constants';
import { Rule, DimensionData } from '../types';
import { levels } from '../levels';
import { EntityManager } from './EntityManager';
import { WordBoxManager } from './WordBoxManager';
import { ParticleManager } from './ParticleManager';
import { RuleParser } from './RuleParser';

export class GameEngine {
  private app: PIXI.Application;
  private gridContainer: PIXI.Container;
  private entityContainer: PIXI.Container;
  private wordContainer: PIXI.Container;
  private lineContainer: PIXI.Container;
  private particleContainer: PIXI.Container;
  private winContainer: PIXI.Container | null = null;

  private entityManager: EntityManager;
  private wordBoxManager: WordBoxManager;
  private particleManager: ParticleManager;
  private ruleParser: RuleParser;

  public currentDimension = 1;
  public currentLevel = 1;
  public rules: Rule[] = [];
  public gameWon = false;
  public isAnimating = false;
  public completedLevels: number[] = [];

  public dimensionData: { [key: number]: DimensionData } = {
    1: { wordBoxes: [], connectedWords: [] },
    2: { wordBoxes: [], connectedWords: [] },
    3: { wordBoxes: [], connectedWords: [] },
    4: { wordBoxes: [], connectedWords: [] }
  };

  public onUpdate?: () => void;

  constructor(app: PIXI.Application) {
    this.app = app;
    
    this.gridContainer = new PIXI.Container();
    this.entityContainer = new PIXI.Container();
    this.wordContainer = new PIXI.Container();
    this.lineContainer = new PIXI.Container();
    this.particleContainer = new PIXI.Container();

    this.app.stage.addChild(this.gridContainer);
    this.app.stage.addChild(this.entityContainer);
    this.app.stage.addChild(this.lineContainer);
    this.app.stage.addChild(this.wordContainer);
    this.app.stage.addChild(this.particleContainer);

    this.entityManager = new EntityManager(this.entityContainer);
    this.wordBoxManager = new WordBoxManager(this.wordContainer);
    this.particleManager = new ParticleManager(this.particleContainer);
    this.ruleParser = new RuleParser(this);

    this.createGrid();
    this.loadCompletedLevels();
    this.startGameLoop();
  }

  private createGrid(): void {
    const grid = new PIXI.Graphics();
    for (let x = 0; x <= GRID_WIDTH; x++) {
      grid.moveTo(x * CELL_SIZE, 0);
      grid.lineTo(x * CELL_SIZE, CANVAS_HEIGHT);
      grid.stroke({ color: 0x00ffff, width: 0.4, alpha: 0.1 });
    }
    for (let y = 0; y <= GRID_HEIGHT; y++) {
      grid.moveTo(0, y * CELL_SIZE);
      grid.lineTo(CANVAS_WIDTH, y * CELL_SIZE);
      grid.stroke({ color: 0x00ffff, width: 0.4, alpha: 0.1 });
    }
    this.gridContainer.addChild(grid);
  }

  private loadCompletedLevels(): void {
    const saved = localStorage.getItem('completedLevels');
    if (saved) {
      this.completedLevels = JSON.parse(saved);
    }
  }

  private saveCompletedLevels(): void {
    localStorage.setItem('completedLevels', JSON.stringify(this.completedLevels));
  }

  public markLevelCompleted(level: number): void {
    if (!this.completedLevels.includes(level)) {
      this.completedLevels.push(level);
      this.saveCompletedLevels();
    }
  }

  public loadLevel(levelNum: number): void {
    this.entityManager.clear();
    this.wordBoxManager.clear();
    this.particleManager.clear();

    for (let dim = 2; dim <= 4; dim++) {
      this.dimensionData[dim].wordBoxes = [];
      this.dimensionData[dim].connectedWords = [];
    }

    if (this.winContainer && this.winContainer.parent) {
      this.app.stage.removeChild(this.winContainer);
      this.winContainer = null;
    }

    this.rules = [];
    this.gameWon = false;
    this.currentDimension = 1;
    this.isAnimating = false;
    this.currentLevel = levelNum;

    const level = levels[levelNum];
    if (!level) return;

    this.entityManager.createEntities(level.entities);
    
    // Set phantom sprite to dimension 1 (blue)
    this.entityManager.updatePhantomTextures(1);

    for (let dim = 2; dim <= 4; dim++) {
      if (level.wordBoxes[dim]) {
        const wordBoxes = this.wordBoxManager.createWordBoxes(level.wordBoxes[dim], dim);
        this.dimensionData[dim].wordBoxes = wordBoxes;
      }
    }

    // Pre-connect essential rules for tutorial levels
    if (levelNum === 1) {
      // Level 1: Connect only PHANTOM IS YOU in dimension 2
      // Player must connect PORTAL IS WIN themselves to learn
      const dim2Boxes = this.dimensionData[2].wordBoxes;
      if (dim2Boxes.length >= 3) {
        // Connect first 3 word boxes: PHANTOM IS YOU
        for (let i = 0; i < 3; i++) {
          dim2Boxes[i].connected = true;
          this.dimensionData[2].connectedWords.push(dim2Boxes[i]);
        }
      }
      
      // Connect BARRIER IS STOP in dimension 3
      const dim3Boxes = this.dimensionData[3].wordBoxes;
      if (dim3Boxes.length >= 3) {
        // Connect first 3 word boxes: BARRIER IS STOP
        for (let i = 0; i < 3; i++) {
          dim3Boxes[i].connected = true;
          this.dimensionData[3].connectedWords.push(dim3Boxes[i]);
        }
      }
    } else if (levelNum === 2) {
      // Level 2: Connect all essential rules
      const dim2Boxes = this.dimensionData[2].wordBoxes;
      if (dim2Boxes.length >= 6) {
        // Connect all 6 word boxes: PHANTOM IS YOU + PORTAL IS WIN
        for (let i = 0; i < 6; i++) {
          dim2Boxes[i].connected = true;
          this.dimensionData[2].connectedWords.push(dim2Boxes[i]);
        }
      }
      
      // Connect BARRIER IS STOP in dimension 3
      const dim3Boxes = this.dimensionData[3].wordBoxes;
      if (dim3Boxes.length >= 3) {
        // Connect first 3 word boxes: BARRIER IS STOP
        for (let i = 0; i < 3; i++) {
          dim3Boxes[i].connected = true;
          this.dimensionData[3].connectedWords.push(dim3Boxes[i]);
        }
      }
    }

    this.parseRules();
    this.drawConnectionLines();
    this.triggerUpdate();
  }

  public parseRules(): void {
    this.ruleParser.parseRules();
    // Update z-indices so player entities render on top
    this.entityManager.updateZIndices(this.rules);
  }

  public drawConnectionLines(): void {
    this.lineContainer.removeChildren();

    const connectedWords = this.dimensionData[this.currentDimension].connectedWords;
    if (connectedWords.length < 2) return;

    const line = new PIXI.Graphics();
    const colors = [0, 0x00FFFF, 0xFF00FF, 0x00FF00, 0xFFAA00];

    for (let i = 0; i < connectedWords.length - 1; i++) {
      const w1 = connectedWords[i];
      const w2 = connectedWords[i + 1];

      const x1 = w1.x * CELL_SIZE + CELL_SIZE / 2;
      const y1 = w1.y * CELL_SIZE + CELL_SIZE / 2;
      const x2 = w2.x * CELL_SIZE + CELL_SIZE / 2;
      const y2 = w2.y * CELL_SIZE + CELL_SIZE / 2;

      const dx = x2 - x1;
      const dy = y2 - y1;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const steps = Math.floor(distance / 4);

      for (let j = 0; j <= steps; j++) {
        const t = j / steps;
        const x = Math.round(x1 + dx * t);
        const y = Math.round(y1 + dy * t);

        line.rect(x - 1, y - 1, 2, 2);
        line.fill({ color: colors[this.currentDimension] });
      }
    }

    this.lineContainer.addChild(line);
  }

  public switchDimension(dim: number): void {
    if (dim === this.currentDimension || dim < 1 || dim > 4) return;

    // Fade out animation
    const fadeOut = () => {
      this.entityContainer.alpha -= 0.15;
      if (this.entityContainer.alpha > 0.1) {
        requestAnimationFrame(fadeOut);
      } else {
        // Switch dimension at minimum alpha
        this.currentDimension = dim;
        this.entityManager.updatePhantomTextures(dim);
        this.drawConnectionLines();
        this.triggerUpdate();
        
        // Fade in animation
        const fadeIn = () => {
          this.entityContainer.alpha += 0.15;
          if (this.entityContainer.alpha < 1) {
            requestAnimationFrame(fadeIn);
          } else {
            this.entityContainer.alpha = 1;
          }
        };
        fadeIn();
      }
    };
    fadeOut();

    // Create dimension transition particles
    for (let i = 0; i < 20; i++) {
      setTimeout(() => {
        const colors = [0, 0x00FFFF, 0xFF00FF, 0x00FF00, 0xFFAA00];
        this.particleManager.createParticles(
          Math.random() * CANVAS_WIDTH,
          Math.random() * CANVAS_HEIGHT,
          colors[dim],
          3
        );
      }, i * 15);
    }
  }

  public handleMove(dx: number, dy: number): void {
    if (this.isAnimating || this.gameWon) return;

    if (this.currentDimension !== 1) {
      this.handleWordBoxMove(dx, dy);
    } else {
      this.handleEntityMove(dx, dy);
    }
  }

  private handleWordBoxMove(dx: number, dy: number): void {
    const playerEntity = this.entityManager.getEntity('PHANTOM');
    if (!playerEntity) return;

    const newX = playerEntity.x + dx;
    const newY = playerEntity.y + dy;

    if (newX < 0 || newX >= GRID_WIDTH || newY < 0 || newY >= GRID_HEIGHT) return;

    playerEntity.moveTo(newX, newY);
    this.isAnimating = true;

    const wordBoxes = this.dimensionData[this.currentDimension].wordBoxes;
    const connectedWords = this.dimensionData[this.currentDimension].connectedWords;

    for (const word of wordBoxes) {
      if (word.x === newX && word.y === newY) {
        if (word.connected) {
          const index = connectedWords.indexOf(word);
          if (index !== -1) {
            const removed = connectedWords.splice(index);
            removed.forEach((w: any) => w.connected = false);
            this.particleManager.createParticles(
              newX * CELL_SIZE + CELL_SIZE / 2,
              newY * CELL_SIZE + CELL_SIZE / 2,
              0xFF0000,
              15
            );
            this.drawConnectionLines();
            this.parseRules();
          }
        } else {
          word.connected = true;
          connectedWords.push(word);
          this.particleManager.createParticles(
            newX * CELL_SIZE + CELL_SIZE / 2,
            newY * CELL_SIZE + CELL_SIZE / 2,
            0xFF00FF,
            12
          );
          this.drawConnectionLines();
          this.parseRules();
        }
        break;
      }
    }
  }

  private handleEntityMove(dx: number, dy: number): void {
    const youEntities = this.entityManager.getYouEntities(this.rules);
    if (youEntities.length === 0) return;

    let moved = false;
    for (const entity of youEntities) {
      if (this.entityManager.moveEntity(entity, dx, dy, this.rules)) {
        moved = true;
      }
    }

    if (moved) {
      this.isAnimating = true;
      this.checkWinCondition();
    }
  }

  private checkWinCondition(): void {
    if (this.gameWon || this.currentDimension !== 1) return;

    const youEntities = this.entityManager.getYouEntities(this.rules);
    for (const you of youEntities) {
      // Check at target position to handle collision during movement animation
      const entitiesHere = this.entityManager.getEntitiesAt(you.targetX, you.targetY);
      for (const ent of entitiesHere) {
        if (ent !== you && this.ruleParser.hasProperty(ent, 'WIN')) {
          this.gameWon = true;
          this.startWinAnimation(you, ent);
          return;
        }
      }
    }
  }

  private startWinAnimation(player: any, portal: any): void {
    // Suck-in animation: pull player toward portal center
    let animationFrame = 0;
    const maxFrames = 30;
    const startX = player.sprite.x;
    const startY = player.sprite.y;
    const endX = portal.x * CELL_SIZE + CELL_SIZE / 2;
    const endY = portal.y * CELL_SIZE + CELL_SIZE / 2;
    
    const animate = () => {
      animationFrame++;
      const progress = animationFrame / maxFrames;
      const easeProgress = 1 - Math.pow(1 - progress, 3); // Ease-in cubic
      
      // Move player toward portal center
      player.sprite.x = startX + (endX - startX) * easeProgress;
      player.sprite.y = startY + (endY - startY) * easeProgress;
      
      // Shrink and rotate player
      const scale = 1 - easeProgress * 0.8;
      player.sprite.scale.set(scale);
      player.sprite.rotation = easeProgress * Math.PI * 4;
      player.sprite.alpha = 1 - easeProgress * 0.5;
      
      // Create spiral particles
      if (animationFrame % 2 === 0) {
        const angle = (animationFrame / maxFrames) * Math.PI * 8;
        const radius = (1 - easeProgress) * CELL_SIZE;
        const px = endX + Math.cos(angle) * radius;
        const py = endY + Math.sin(angle) * radius;
        this.particleManager.createParticles(px, py, 0xFF00FF, 3);
      }
      
      if (animationFrame < maxFrames) {
        requestAnimationFrame(animate);
      } else {
        // Reset player appearance and show win message
        player.sprite.scale.set(1);
        player.sprite.rotation = 0;
        player.sprite.alpha = 1;
        this.markLevelCompleted(this.currentLevel);
        this.showWinMessage();
      }
    };
    
    animate();
  }

  private showWinMessage(): void {
    this.winContainer = new PIXI.Container();

    const bg = new PIXI.Graphics();
    bg.rect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    bg.fill({ color: 0x000000, alpha: 0.95 });
    this.winContainer.addChild(bg);

    const text = new PIXI.Text({
      text: `★ LEVEL ${this.currentLevel} COMPLETE ★\n${levels[this.currentLevel].name}`,
      style: {
        fontSize: 28,
        fill: 0x00FFFF,
        fontFamily: 'Courier New, monospace',
        fontWeight: 'bold',
        align: 'center',
        stroke: { color: 0xFF00FF, width: 3 }
      }
    });
    text.anchor.set(0.5);
    text.x = CANVAS_WIDTH / 2;
    text.y = CANVAS_HEIGHT / 2 - 15;
    this.winContainer.addChild(text);

    const subtext = new PIXI.Text({
      text: `Rules Created: ${this.rules.length}`,
      style: {
        fontSize: 14,
        fill: 0xFFFFFF,
        fontFamily: 'Courier New, monospace',
        fontWeight: 'bold',
      }
    });
    subtext.anchor.set(0.5);
    subtext.x = CANVAS_WIDTH / 2;
    subtext.y = CANVAS_HEIGHT / 2 + 20;
    this.winContainer.addChild(subtext);

    if (this.currentLevel < 10) {
      const nextButton = new PIXI.Graphics();
      nextButton.rect(0, 0, 160, 40);
      nextButton.fill({ color: 0x00FF00, alpha: 0.2 });
      nextButton.stroke({ color: 0x00FF00, width: 2 });
      nextButton.x = CANVAS_WIDTH / 2 - 80;
      nextButton.y = CANVAS_HEIGHT / 2 + 45;
      this.winContainer.addChild(nextButton);

      const nextText = new PIXI.Text({
        text: 'NEXT LEVEL',
        style: {
          fontSize: 14,
          fill: 0x00FF00,
          fontFamily: 'Courier New, monospace',
          fontWeight: 'bold',
        }
      });
      nextText.anchor.set(0.5);
      nextText.x = CANVAS_WIDTH / 2;
      nextText.y = CANVAS_HEIGHT / 2 + 65;
      this.winContainer.addChild(nextText);

      nextButton.eventMode = 'static';
      nextButton.cursor = 'pointer';
      nextButton.on('pointerdown', () => {
        this.loadLevel(this.currentLevel + 1);
      });

      nextButton.on('pointerover', () => {
        nextButton.clear();
        nextButton.rect(0, 0, 160, 40);
        nextButton.fill({ color: 0x00FF00, alpha: 0.3 });
        nextButton.stroke({ color: 0x00FF00, width: 2 });
      });

      nextButton.on('pointerout', () => {
        nextButton.clear();
        nextButton.rect(0, 0, 160, 40);
        nextButton.fill({ color: 0x00FF00, alpha: 0.2 });
        nextButton.stroke({ color: 0x00FF00, width: 2 });
      });
    } else {
      const completeText = new PIXI.Text({
        text: 'All levels completed!\nYou are a 4D Master!',
        style: {
          fontSize: 12,
          fill: 0xFFD700,
          fontFamily: 'Courier New, monospace',
          fontWeight: 'bold',
          align: 'center',
        }
      });
      completeText.anchor.set(0.5);
      completeText.x = CANVAS_WIDTH / 2;
      completeText.y = CANVAS_HEIGHT / 2 + 45;
      this.winContainer.addChild(completeText);
    }

    this.app.stage.addChild(this.winContainer);

    for (let i = 0; i < 40; i++) {
      setTimeout(() => {
        this.particleManager.createParticles(
          Math.random() * CANVAS_WIDTH,
          Math.random() * CANVAS_HEIGHT,
          [0x00FFFF, 0xFF00FF, 0x00FF00, 0xFFAA00][Math.floor(Math.random() * 4)],
          3
        );
      }, i * 20);
    }
  }

  private startGameLoop(): void {
    this.app.ticker.add(() => {
      this.particleManager.update();
      
      let allDone = true;
      if (!this.entityManager.updateAll()) {
        allDone = false;
      }

      if (allDone && this.isAnimating) {
        this.isAnimating = false;
        // Check win condition after movement animation completes
        this.checkWinCondition();
      }

      for (let dim = 2; dim <= 4; dim++) {
        this.dimensionData[dim].wordBoxes.forEach((w: any) => w.update(this.currentDimension));
      }

      // Ambient dimension particles
      if (this.currentDimension !== 1 && Math.random() < 0.05 && this.particleManager.count() < 40) {
        const colors = [0, 0x00FFFF, 0xFF00FF, 0x00FF00, 0xFFAA00];
        this.particleManager.createParticles(
          Math.random() * CANVAS_WIDTH,
          Math.random() * CANVAS_HEIGHT,
          colors[this.currentDimension],
          1
        );
      }
      
      // WIN portal particles
      if (this.currentDimension === 1 && Math.random() < 0.15 && this.particleManager.count() < 60) {
        const winPortal = this.entityManager.getEntities().find((e: any) => e.type === 'PORTAL' && e.isWinPortal);
        if (winPortal) {
          const angle = Math.random() * Math.PI * 2;
          const radius = CELL_SIZE / 2 + Math.random() * 10;
          const px = winPortal.x * CELL_SIZE + CELL_SIZE / 2 + Math.cos(angle) * radius;
          const py = winPortal.y * CELL_SIZE + CELL_SIZE / 2 + Math.sin(angle) * radius;
          this.particleManager.createParticles(px, py, 0xFF00FF, 2);
        }
      }
    });
  }

  private triggerUpdate(): void {
    if (this.onUpdate) {
      this.onUpdate();
    }
  }

  public destroy(): void {
    this.app.destroy(true, { children: true });
  }
}
